import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from 'react-query';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { ExclamationCircleIcon, PhotoIcon } from '@heroicons/react/24/outline';

const CategoryForm = ({ category, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'tour'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        type: category.type || 'tour'
      });
      
      // Set image preview if category has an image
      if (category.image) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const imageUrl = category.image.startsWith('http') 
          ? category.image 
          : `${apiUrl}/storage/${category.image}`;
        setImagePreview(imageUrl);
      }
    }
  }, [category]);

  const createCategoryMutation = useMutation(
    async (data) => {
      // Create FormData object for file upload
      const formDataObj = new FormData();
      
      // Add all form fields to FormData
      Object.keys(data).forEach(key => {
        if (key !== 'image') {
          formDataObj.append(key, data[key]);
        }
      });
      
      // Add image file if it exists
      if (imageFile) {
        formDataObj.append('image', imageFile);
      }
      
      return await api.post('/api/v1/admin/categories', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    {
      onSuccess: () => {
        toast.success('Category created successfully');
        queryClient.invalidateQueries('categories');
        if (onSubmit) onSubmit();
      },
      onError: (error) => {
        console.error('Error creating category:', error);
        
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          toast.error('Failed to create category. Please try again.');
        }
        
        setIsSubmitting(false);
      }
    }
  );

  const updateCategoryMutation = useMutation(
    async (data) => {
      try {
        // Create FormData object for file upload
        const formDataObj = new FormData();
        
        // Add all form fields to FormData
        Object.keys(data).forEach(key => {
          if (key !== 'image') {
            formDataObj.append(key, data[key]);
          }
        });
        
        // Add image file if it exists
        if (imageFile) {
          formDataObj.append('image', imageFile);
        }
        
        // Use POST with _method=PUT for FormData
        formDataObj.append('_method', 'PUT');
        
        console.log('Updating category with ID:', category.id);
        
        return await api.post(`/api/v1/admin/categories/${category.id}`, formDataObj, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Accept': 'application/json',
            'X-HTTP-Method-Override': 'PUT'
          }
        });
      } catch (error) {
        console.error('Error in update mutation:', error);
        throw error;
      }
    },
    {
      onSuccess: () => {
        toast.success('Category updated successfully');
        queryClient.invalidateQueries('categories');
        if (onSubmit) onSubmit();
      },
      onError: (error) => {
        console.error('Error updating category:', error);
        
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          toast.error('Failed to update category. Please try again.');
        }
        
        setIsSubmitting(false);
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Clear any image-related errors
      if (errors.image) {
        setErrors(prev => ({ ...prev, image: null }));
      }
    }
  };
  
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    if (category) {
      updateCategoryMutation.mutate(formData);
    } else {
      createCategoryMutation.mutate(formData);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {category ? 'Edit Category' : 'Add New Category'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {category 
                ? 'Update the details for this category.' 
                : 'Create a new category that can be used when creating tours.'}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Category Name*
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`block w-full rounded-md sm:text-sm ${
                    errors.name 
                      ? 'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-primary focus:border-primary'
                  }`}
                  placeholder="e.g. Beach Tours"
                />
                {errors.name && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div>
                )}
              </div>
              {errors.name && (
                <p className="mt-2 text-sm text-red-600">{errors.name}</p>
              )}
            </div>



            <div className="sm:col-span-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1">
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Brief description of this category"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Provide a brief description of this category to help tour creators.
              </p>
            </div>
            
            {/* Featured Image Upload */}
            <div className="sm:col-span-6">
              <label htmlFor="image" className="block text-sm font-medium text-gray-700">
                Featured Image
              </label>
              <div className="mt-1 flex items-center space-x-4">
                {imagePreview ? (
                  <div className="relative group">
                    <img 
                      src={imagePreview} 
                      alt="Category preview" 
                      className="h-32 w-48 object-cover rounded-md border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="h-32 w-48 border-2 border-gray-300 border-dashed rounded-md flex items-center justify-center">
                    <PhotoIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                
                <div className="flex-1">
                  <input
                    id="image"
                    name="image"
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageChange}
                    className="sr-only"
                  />
                  <label
                    htmlFor="image"
                    className="cursor-pointer py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    {imagePreview ? 'Change Image' : 'Upload Image'}
                  </label>
                  {errors.image && (
                    <p className="mt-2 text-sm text-red-600">{errors.image}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Recommended size: 800x600px. JPG, PNG or GIF. Max 2MB.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {category ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              category ? 'Update Category' : 'Create Category'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

CategoryForm.propTypes = {
  category: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default CategoryForm;
